<template>
  <Header
    :title="seoH1"
    :subtitle="seoH2"
    :loading="loading"
    events-identifier="articles-sights"
    :city="geoData.city"
    :country="geoData.country"
  />

  <Wrap>
    <SightsListWrapper
      :subject-geo-id="cityId"
      :subject-geo-name="cityName"
      :general-page-data-loading="loading"
    >
      <SightsList
        :subject-geo-id="cityId"
        :subject-geo-name="cityName"
        :general-page-data-loading="loading"
        pagination
      />
    </SightsListWrapper>
  </Wrap>

  <ExperiencesList
    class="exp-list"
    :subject-geo-id="cityId"
    :subject-geo-name="cityName"
    :in-obj-phrase="inObjPhrase"
    :general-page-data-loading="loading"
  >
    <ExperienceListing
      :skeletons-count="3"
      more-button
      :more-button-text="t('seeAll')"
      :more-button-link="allExperiencesLink"
      :general-page-data-loading="loading"
      analytic-events-prefix="articles-sights-info"
      @load-more="showAllExperiences"
    />
  </ExperiencesList>

  <SubscriptionBlock
    api-url=""
    :analytics-api-url="VITE_RUNTIME_SOGU_SERVICE_URL"
  />

  <SeoBlock v-if="seoText">
    <Seo />
  </SeoBlock>
</template>

<script lang="ts" setup>
  import { useI18n } from '@experience/i18n';
  import { SubscriptionBlock } from '@experience/subscription-block-package';
  import { useHead } from '@unhead/vue';
  import { useRuntimeConfig } from 'virtual:runtime-config';
  import { computed, onBeforeMount, onBeforeUnmount } from 'vue';
  import { useRoute } from 'vue-router';

  import { trackEvent } from '@/services/analytics';
  import { MF_NAME } from '@/app/const';
  import ExperienceListing from '@/features/ExperienceListing.vue';
  import Seo from '@/features/Seo.vue';
  import { useExperienceEntity } from '@/entities/experience';
  import { useSeoEntity } from '@/entities/seo';
  import { useSightEntity } from '@/entities/sight';
  import Wrap from '@/shared/ui/Wrap.vue';
  import { tryAwait } from '@/shared/utils/tryAwait';

  import { useCityArticlesEntity } from './entities/cityArticles';

  import { ExperiencesList } from './features/experiencesList';
  import { SightsList, SightsListWrapper } from './features/sightsList';
  import { ru } from './i18n/ru';
  import { Header } from './widgets/articlesHeader';
  import SeoBlock from './widgets/SeoBlock.vue';

  const { t } = useI18n({ inheritLocale: true, messages: { ru } });

  const { VITE_RUNTIME_SOGU_SERVICE_URL } = useRuntimeConfig()[MF_NAME];
  const route = useRoute();

  const seoEntity = useSeoEntity();
  const sightsEntity = useSightEntity();
  const cityArticlesEntity = useCityArticlesEntity();
  const experienceEntity = useExperienceEntity();

  const loading = computed(() => seoEntity.loading || cityArticlesEntity.loading);

  const slug = computed(() => String(route.params.slug));
  const cityName = computed(() => cityArticlesEntity.city?.nameRu ?? '');
  const cityId = computed(() => cityArticlesEntity.city?.id ?? 0);

  const title = computed(() => seoEntity.seo?.title ?? '');
  const description = computed(() => seoEntity.seo?.description ?? '');
  const ogImage = computed(() => cityArticlesEntity?.city?.image.cover ?? '');

  const inObjPhrase = computed(() => cityArticlesEntity.city?.inObjPhrase ?? '');

  const seoText = computed(() => seoEntity.seo?.text ?? '');

  const seoH1 = computed(() => seoEntity.seo?.h1 ?? '');
  const seoH2 = computed(() => seoEntity.seo?.h2 ?? '');

  const geoData = computed(() => {
    const { city } = cityArticlesEntity;

    return {
      city: {
        name: city?.nameRu || '',
        slug: city?.slug || '',
      },
      country: {
        name: city?.country.nameRu || '',
        slug: city?.country.slug || '',
      },
    };
  });

  const getGeneralEventParams = () =>
    ({
      subject_geo_id: cityId.value,
      subject_geo_name: cityName.value,
      subject_geo_type: 'city',
    }) as const;

  useHead({
    title,
    link: [
      {
        rel: 'canonical',
        href: `${window.location.origin}${window.location.pathname}/`,
      },
    ],
    meta: [
      {
        name: 'description',
        content: () => description.value,
      },
      {
        property: 'og:title',
        content: () => title.value,
      },
      {
        property: 'og:description',
        content: () => description.value,
      },
      {
        content: () => ogImage.value,
        property: 'og:image',
      },
      {
        name: 'twitter:card',
        content: () => (ogImage.value ? 'summary_large_image' : undefined),
      },
      {
        name: 'twitter:image',
        content: () => ogImage.value,
      },
    ],
  });

  const fetchData = async () => {
    const citySlugs = [slug.value];
    const seoDataPromises = [
      cityArticlesEntity.loadPageData(slug.value),
      seoEntity.loadSeoAttraction(slug.value, 'city'),
    ];

    (async () => {
      await Promise.allSettled(seoDataPromises);
      trackEvent('articles-sights-info.pageview', getGeneralEventParams());
    })();

    await Promise.allSettled([
      ...seoDataPromises,
      sightsEntity.getSights({ pageSize: 20, citySlugs }),
      experienceEntity.load({
        citySlugs,
        limit: 6,
        offset: 0,
        priceFormat: 'travelers',
        extendedMovementTypes: true,
        photosCount: 1,
      }),
    ]);
  };

  onBeforeMount(() => fetchData());

  onBeforeUnmount(() => {
    experienceEntity.reset();
    seoEntity.reset();
  });

  const allExperiencesLink = computed(() => `/experience/${slug.value}/`);

  const showAllExperiences = async () => {
    await tryAwait(() =>
      trackEvent('articles-sights-info:section:show-all.click', {
        ...getGeneralEventParams(),
        section_type: 'experience',
      }),
    );
    // TODO: заменить на routerProxy.push после окончательного перехода на МФ листинга.
    window.location.href = allExperiencesLink.value;
  };
</script>

<style lang="scss" scoped>
  .exp-list {
    margin-top: 48px;

    @include tablet {
      margin-top: 40px;
    }
  }
</style>
